import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, getErrorMessage, type LiveopsEvent } from '../lib/api';

export default function LiveOps() {
  const [events, setEvents] = useState<LiveopsEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.listLiveops();
      setEvents(data.events);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id: string) => {
    if (!confirm(`Delete event "${id}"? This will soft-delete it (is_active = 0).`)) return;
    setDeleteError('');
    try {
      await adminApi.deleteLiveops(id);
      await load();
    } catch (err) {
      setDeleteError(getErrorMessage(err));
    }
  };

  if (loading) return <div className="text-gray-400">Loading live ops events...</div>;
  if (error) return <div className="text-red-400">Error: {error}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Live Ops Events</h2>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded text-sm font-medium"
        >
          {showCreate ? 'Cancel' : '+ New Event'}
        </button>
      </div>

      {showCreate && <CreateEventForm onCreated={() => { setShowCreate(false); load(); }} />}

      {deleteError && (
        <div className="bg-red-900/20 border border-red-800/50 text-red-200 rounded p-3 text-sm">
          {deleteError}
        </div>
      )}

      {events.length === 0 ? (
        <p className="text-gray-500">No live ops events yet. Create one to get started.</p>
      ) : (
        <div className="space-y-3">
          {events.map((evt) => (
            <EventCard key={evt.id} event={evt} onDelete={() => handleDelete(evt.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function EventCard({ event, onDelete }: { event: LiveopsEvent; onDelete: () => void }) {
  const now = Date.now();
  const isActive = event.is_active && event.start_ms <= now && event.end_ms > now;
  const isUpcoming = event.start_ms > now;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">{event.title}</h3>
            <span
              className={`text-xs px-2 py-0.5 rounded ${
                isActive ? 'bg-green-900 text-green-300' :
                isUpcoming ? 'bg-blue-900 text-blue-300' :
                'bg-gray-800 text-gray-400'
              }`}
            >
              {isActive ? 'Active' : isUpcoming ? 'Upcoming' : 'Inactive'}
            </span>
          </div>
          <p className="text-sm text-gray-400 font-mono mt-0.5">{event.id}</p>
          {event.description && <p className="text-sm text-gray-300 mt-1">{event.description}</p>}
        </div>
        <div className="flex gap-3">
          <Link
            to={`/liveops/${encodeURIComponent(event.id)}/preview`}
            className="text-sm text-blue-400 hover:text-blue-300"
          >
            Preview
          </Link>
          <button
            onClick={onDelete}
            className="text-sm text-red-400 hover:text-red-300"
          >
            Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
        <div>
          <span className="text-gray-500">Type:</span> <span>{event.target_type}</span>
        </div>
        <div>
          <span className="text-gray-500">Bonus GS:</span> <span>{event.bonus_gs}</span>
        </div>
        <div>
          <span className="text-gray-500">Premium Bonus:</span> <span>{event.premium_bonus_gs}</span>
        </div>
        <div>
          <span className="text-gray-500">Premium Only:</span> <span>{event.premium_only ? 'Yes' : 'No'}</span>
        </div>
        <div>
          <span className="text-gray-500">Start:</span>{' '}
          <span>{new Date(event.start_ms).toLocaleDateString()}</span>
        </div>
        <div>
          <span className="text-gray-500">End:</span>{' '}
          <span>{new Date(event.end_ms).toLocaleDateString()}</span>
        </div>
        {event.target_type === 'multiplier' && (
          <>
            {event.xp_multiplier > 1 && (
              <div>
                <span className="text-gray-500">XP Mult:</span>{' '}
                <span className="text-orange-400 font-semibold">{event.xp_multiplier}x</span>
              </div>
            )}
            {event.gs_multiplier > 1 && (
              <div>
                <span className="text-gray-500">GS Mult:</span>{' '}
                <span className="text-purple-400 font-semibold">{event.gs_multiplier}x</span>
              </div>
            )}
            {event.region_filter && (
              <div>
                <span className="text-gray-500">Region:</span> <span>{event.region_filter}</span>
              </div>
            )}
            <div>
              <span className="text-gray-500">Participants:</span> <span>{event.participant_count}</span>
            </div>
          </>
        )}
        {event.target_type === 'venue_set' && (
          <div className="col-span-2">
            <span className="text-gray-500">Venues:</span>{' '}
            <span className="font-mono text-xs">{event.venue_ids.join(', ')}</span>
          </div>
        )}
        {event.dish_targets && event.dish_targets.length > 0 && (
          <div className="col-span-2">
            <span className="text-gray-500">Dish Targets:</span>{' '}
            <span className="text-xs">
              {event.dish_targets.map((d) => `${d.dish_name} (${d.required_count})`).join(', ')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function CreateEventForm({ onCreated }: { onCreated: () => void }) {
  const [title, setTitle] = useState('');
  const [targetType, setTargetType] = useState<'venue_set' | 'dish_set' | 'multi_dish' | 'multiplier'>('venue_set');
  const [venueIds, setVenueIds] = useState('');
  const [bonusGs, setBonusGs] = useState('100');
  const [premiumBonusGs, setPremiumBonusGs] = useState('200');
  const [durationDays, setDurationDays] = useState('7');
  const [xpMultiplier, setXpMultiplier] = useState('2.0');
  const [gsMultiplier, setGsMultiplier] = useState('1.0');
  const [regionFilter, setRegionFilter] = useState('');
  const [emoji, setEmoji] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const now = Date.now();
      const days = parseInt(durationDays, 10) || 7;
      const body: Parameters<typeof adminApi.createLiveops>[0] = {
        title: title.trim(),
        target_type: targetType,
        bonus_gs: parseInt(bonusGs, 10) || 0,
        premium_bonus_gs: parseInt(premiumBonusGs, 10) || 0,
        start_ms: now,
        end_ms: now + days * 24 * 60 * 60 * 1000,
      };
      if (targetType === 'venue_set') {
        body.venue_ids = venueIds.split(',').map((v) => v.trim()).filter(Boolean);
        if (body.venue_ids.length < 3) {
          setError('venue_set requires at least 3 venue IDs (comma-separated)');
          setSubmitting(false);
          return;
        }
      }
      if (targetType === 'multiplier') {
        body.xp_multiplier = parseFloat(xpMultiplier) || 1.0;
        body.gs_multiplier = parseFloat(gsMultiplier) || 1.0;
        if (body.xp_multiplier <= 1.0 && body.gs_multiplier <= 1.0) {
          setError('multiplier requires xp_multiplier or gs_multiplier > 1.0');
          setSubmitting(false);
          return;
        }
        if (regionFilter.trim()) body.region_filter = regionFilter.trim();
        if (emoji.trim()) body.emoji = emoji.trim();
      }
      await adminApi.createLiveops(body);
      onCreated();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3">
      <h3 className="text-lg font-semibold">Create New Event</h3>
      {error && <p className="text-sm text-red-400">{error}</p>}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Title</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm"
            required
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Target Type</label>
          <select
            value={targetType}
            onChange={(e) => setTargetType(e.target.value as typeof targetType)}
            className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm"
          >
            <option value="venue_set">Venue Set</option>
            <option value="dish_set">Dish Set</option>
            <option value="multi_dish">Multi Dish</option>
            <option value="multiplier">Multiplier (XP/GS)</option>
          </select>
        </div>
        {targetType === 'venue_set' && (
          <div className="col-span-2">
            <label className="block text-sm text-gray-400 mb-1">
              Venue IDs (comma-separated, min 3)
            </label>
            <input
              value={venueIds}
              onChange={(e) => setVenueIds(e.target.value)}
              placeholder="v_abc123, v_def456, v ghi789"
              className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm font-mono"
            />
          </div>
        )}
        {targetType === 'multiplier' && (
          <>
            <div>
              <label className="block text-sm text-gray-400 mb-1">XP Multiplier</label>
              <input
                type="number"
                step="0.1"
                min="1.0"
                max="10.0"
                value={xpMultiplier}
                onChange={(e) => setXpMultiplier(e.target.value)}
                className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">GS Multiplier</label>
              <input
                type="number"
                step="0.1"
                min="1.0"
                max="10.0"
                value={gsMultiplier}
                onChange={(e) => setGsMultiplier(e.target.value)}
                className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Region Filter (optional)</label>
              <input
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
                placeholder="e.g. Kadıköy (leave empty for global)"
                className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Emoji (optional)</label>
              <input
                value={emoji}
                onChange={(e) => setEmoji(e.target.value)}
                placeholder="e.g. ⚡"
                maxLength={16}
                className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm"
              />
            </div>
          </>
        )}
        <div>
          <label className="block text-sm text-gray-400 mb-1">Bonus GS</label>
          <input
            type="number"
            value={bonusGs}
            onChange={(e) => setBonusGs(e.target.value)}
            className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Premium Bonus GS</label>
          <input
            type="number"
            value={premiumBonusGs}
            onChange={(e) => setPremiumBonusGs(e.target.value)}
            className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Duration (days)</label>
          <input
            type="number"
            value={durationDays}
            onChange={(e) => setDurationDays(e.target.value)}
            className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm"
          />
        </div>
      </div>
      <button
        type="submit"
        disabled={submitting}
        className="px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white rounded text-sm font-medium"
      >
        {submitting ? 'Creating...' : 'Create Event'}
      </button>
    </form>
  );
}
